import React, { useEffect, useRef } from 'react';
import useReservationAiSession from '../../hooks/useReservationAiSession';
import CaptainChoiceButtons from '../../virtual-captain/components/CaptainChoiceButtons';
import CaptainChatInput, { type CaptainChatInputHandle } from '../../virtual-captain/components/CaptainChatInput';
import { CaptainAvatar } from '../../virtual-captain/components/CaptainParts';
import '../../virtual-captain/styles/captain.css';
import './PeppyReservationAssistant.css';
import { formatReservationDateTime } from '../../utils/reservationDateTime';

interface PeppyReservationAssistantProps {
  appName: string;
  restaurantId: string;
  restaurantName: string;
  userId: number | null;
  guestName?: string;
  guestPhone?: string;
  locations?: { id: number; name: string }[];
  onConfirmed?: (reservation?: {
    bookingDateTime?: string;
  }) => void;
}

const PeppyReservationAssistant: React.FC<PeppyReservationAssistantProps> = ({
  appName,
  restaurantId,
  restaurantName,
  userId,
  guestName,
  guestPhone,
  locations,
  onConfirmed,
}) => {
  const inputRef = useRef<CaptainChatInputHandle>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const confirmedNotifiedRef = useRef(false);

  const {
    connected,
    connectionFailed,
    messages,
    streaming,
    confirmed,
    sendUserMessage,
    submitChoiceSelection,
  } = useReservationAiSession({
    enabled: Boolean(appName && restaurantId),
    appName,
    restaurantId,
    restaurantName,
    userId,
    guestName,
    guestPhone,
    locations,
  });

  useEffect(() => {
    if (confirmed && onConfirmed && !confirmedNotifiedRef.current) {
      confirmedNotifiedRef.current = true;
      onConfirmed(confirmed);
    }
  }, [confirmed, onConfirmed]);

  useEffect(() => {
    const el = scrollRef.current;
    if (el) {
      el.scrollTop = el.scrollHeight;
    }
  }, [messages, streaming]);

  return (
    <section className="peppy-reservation-panel" aria-label="Peppy AI table booking">
      <header className="peppy-reservation-panel__header">
        <CaptainAvatar size={38} ring />
        <div className="peppy-reservation-panel__brand">
          <div className="peppy-reservation-panel__title">Peppy AI</div>
          <div className="peppy-reservation-panel__subtitle">Table booking · {restaurantName}</div>
        </div>
      </header>

      <div className="peppy-reservation-panel__body" ref={scrollRef}>
        {connectionFailed && (
          <p className="peppy-reservation-panel__status peppy-reservation-panel__status--error">
            Could not connect to Peppy. Use the manual form below.
          </p>
        )}
        {!connectionFailed && !connected && messages.length === 0 && (
          <p className="peppy-reservation-panel__status">Connecting to Peppy…</p>
        )}

        {messages.map((msg) => (
          <div
            key={msg.id}
            className={`peppy-reservation-turn peppy-reservation-turn--${msg.role}`}
          >
            {msg.role === 'assistant' && (
              <div className="peppy-reservation-bubble peppy-reservation-bubble--assistant">
                {msg.text}
              </div>
            )}
            {msg.role === 'user' && (
              <div className="peppy-reservation-bubble peppy-reservation-bubble--user">
                {msg.text}
              </div>
            )}
            {msg.choicePrompt && (
              <CaptainChoiceButtons
                choice={msg.choicePrompt}
                disabled={msg.choiceAnswered || Boolean(confirmed)}
                onSelect={(values) => submitChoiceSelection(msg.id, values)}
              />
            )}
          </div>
        ))}

        {confirmed && (
          <div className="peppy-reservation-confirmed-card">
            <strong>Reservation confirmed</strong>
            <ul>
              {confirmed.fullName && <li>Name: {confirmed.fullName}</li>}
              {confirmed.phoneNumber && <li>Phone: {confirmed.phoneNumber}</li>}
              {confirmed.totalSeats != null && <li>Guests: {confirmed.totalSeats}</li>}
              {confirmed.occassion && <li>Occasion: {confirmed.occassion}</li>}
              {confirmed.bookingDateTime && (
                <li>When: {formatReservationDateTime(confirmed.bookingDateTime)}</li>
              )}
            </ul>
          </div>
        )}

        {streaming && (
          <div className="peppy-reservation-turn peppy-reservation-turn--assistant">
            <div className="peppy-reservation-bubble peppy-reservation-bubble--assistant peppy-reservation-bubble--typing">
              Peppy is typing…
            </div>
          </div>
        )}
      </div>

      {!confirmed && (
        <footer className="peppy-reservation-panel__footer">
          <CaptainChatInput
            ref={inputRef}
            disabled={!connected || streaming}
            onSend={sendUserMessage}
          />
        </footer>
      )}
    </section>
  );
};

export default PeppyReservationAssistant;
